import React, { useState, useMemo } from "react";
import {
  Modal,
  Box,
  Button,
  SpaceBetween,
  Header,
  Container,
  BreadcrumbGroup,
  Table,
  Icon,
} from "@amzn/awsui-components-react";
import { Document } from "models/schemas";
import styles from "./style.module.scss";
import { MoveFoldersModalProps, NavigationItem } from "./definitions";
import { getFileNameWithExtension } from "components/documents-table/utils";

const MoveFoldersModal: React.FC<MoveFoldersModalProps> = ({
  visible,
  setVisible,
  selectedFiles,
  allDocuments,
  teamName,
  onMove,
}) => {
  const [currentPath, setCurrentPath] = useState<NavigationItem[]>([
    { text: "Team files", parentId: teamName },
  ]);
  const [isMoving, setIsMoving] = useState(false);

  const currentParentIdForFolders =
    currentPath[currentPath.length - 1]?.parentId || teamName;

  // Get folders in current directory
  const foldersInCurrentPath = useMemo(() => {
    return allDocuments.filter(
      (doc) =>
        doc.extentionType === "Folder" &&
        doc.parentId === currentParentIdForFolders
    );
  }, [allDocuments, currentParentIdForFolders]);

  // Handle folder navigation
  const handleFolderClick = (folder: Document) => {
    const folderPath = `${currentParentIdForFolders}/${folder.fileName}`;
    setCurrentPath((prev) => [
      ...prev,
      {
        text: folder.fileName,
        parentId: folderPath,
      },
    ]);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (targetIndex: number) => {
    setCurrentPath((prev) => prev.slice(0, targetIndex + 1));
  };

  // Handle move operation
  const handleMove = async () => {
    setIsMoving(true);
    try {
      await onMove(selectedFiles, currentParentIdForFolders);
      setVisible(false);
      // Reset to root path for next time
      setCurrentPath([{ text: "Team files", parentId: teamName }]);
    } catch (error) {
      console.error("Move operation failed:", error);
    } finally {
      setIsMoving(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    // Reset to root path
    setCurrentPath([{ text: "Team files", parentId: teamName }]);
  };

  // Breadcrumb items for navigation
  const breadcrumbItems = currentPath.map((item, index) => ({
    text: item.text,
    href: "#",
    onClick: () => {
      handleBreadcrumbClick(index);
    },
  }));

  // Table columns for folder display
  const folderColumnDefinitions = [
    {
      id: "fileName",
      header: "Folder Name",
      cell: (item: Document) => (
        <Button
          variant="link"
          onClick={() => handleFolderClick(item)}
          ariaLabel={`Navigate to ${item.fileName} folder`}
        >
          <Icon name="folder" />
          <span> {item.fileName}</span>
        </Button>
      ),
      sortingField: "fileName",
      isRowHeader: true,
    },
  ];

  return (
    <Modal
      onDismiss={handleDismiss}
      visible={visible}
      header="Move Files"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleMove}
              loading={isMoving}
              disabled={
                isMoving ||
                selectedFiles.every(
                  (item) => item.parentId === currentPath.at(-1)?.parentId
                )
              }
              disabledReason="Selected files are already in this folder"
            >
              Move to here
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="medium"
    >
      <SpaceBetween size="l">
        {/* File selection summary */}
        <Container>
          <Header>Files to move</Header>
          <Box>
            <ul className={styles.fileList}>
              {selectedFiles.map((file) => (
                <li key={file.documentId}>{getFileNameWithExtension(file)}</li>
              ))}
            </ul>
          </Box>
        </Container>

        {/* Navigation */}
        <Container>
          <Header>Select destination folder</Header>
          <SpaceBetween size="s">
            <Box>
              <strong>Current location:</strong>
              <BreadcrumbGroup
                items={breadcrumbItems}
                onClick={(detail) => {
                  detail.detail.item.onClick();
                }}
              />
            </Box>

            {/* Folder list */}
            {foldersInCurrentPath.length > 0 ? (
              <Table
                columnDefinitions={folderColumnDefinitions}
                items={foldersInCurrentPath}
                loadingText="Loading folders"
                sortingDisabled={false}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No folders found</b>
                  </Box>
                }
                header={
                  <Header>
                    Available folders ({foldersInCurrentPath.length})
                  </Header>
                }
              />
            ) : (
              <Box textAlign="center" color="inherit" padding="l">
                <b>No subfolders in this location</b>
              </Box>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Modal>
  );
};

export default MoveFoldersModal;
